<template>
  <section class="mini-kasko-order__card mt-4">
    <section v-if="errorMessage" ref="errorAlert" class="mini-kasko-order__alert mini-kasko-order__alert--error" tabindex="-1">
      {{ errorMessage }}
    </section>

    <div class="mini-kasko-order__otp">
      <div class="mini-kasko-order__otp-main">
        <h2 class="mini-kasko-order__group-title">Підтвердження OTP</h2>
        <p class="mini-kasko-order__subtitle">
          Оберіть канал відправки OTP-коду, а після отримання введіть код для підтвердження.
          <template v-if="policyNumber">
            Номер поліса: <strong>{{ policyNumber }}</strong>
          </template>
        </p>
      </div>

      <div class="mini-kasko-order__otp-form">
        <div v-if="!isOtpVerified" class="mini-kasko-order__otp-send">
          <div class="mini-kasko-order__otp-options">
            <label
              class="mini-kasko-order__otp-option"
              :class="{ 'mini-kasko-order__otp-option--active': otpSendingChannel === 'email' }"
            >
              <input
                v-model="otpSendingChannel"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="email"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>Email</span>
            </label>
            <label
              class="mini-kasko-order__otp-option"
              :class="{ 'mini-kasko-order__otp-option--active': otpSendingChannel === 'viber_sms' }"
            >
              <input
                v-model="otpSendingChannel"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="viber_sms"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>Viber/SMS</span>
            </label>
          </div>
          <button
            v-if="otpSendingMessage"
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || isOtpSending"
            @click="sendOtp()"
          >
            {{ isOtpSending ? 'Відправка...' : 'Відправити код повторно' }}
          </button>
          <button
            v-else
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || isOtpSending"
            @click="sendOtp()"
          >
            {{ isOtpSending ? 'Відправка...' : 'Відправити код' }}
          </button>
          <small
            class="mini-kasko-order__otp-send-message"
            :class="{ 'mini-kasko-order__otp-send-message--hidden': !otpSendingMessage }"
          >
            {{ otpSendingMessage }}
          </small>
        </div>
        <label class="mini-kasko-order__field">
          <span>OTP-код</span>
          <button
            v-if="isOtpVerified"
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || !otp || isOtpVerified"
            @click="verifyOtp()"
          >
            {{ isOtpVerifying ? 'Перевірка...' : (isOtpVerified ? 'OTP перевірено' : 'Перевірити OTP') }}
          </button>
          <input
            v-else
            v-model.trim="otp"
            type="text"
            :placeholder="otpInputPlaceholder"
            maxlength="12"
            @input="handleOtpInput()"
          >
          <small v-if="otpFieldError" class="mini-kasko-order__field-error">{{ otpFieldError }}</small>
        </label>
        <button
          v-if="!isOtpVerified"
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--secondary"
          :disabled="isProcessing || !otp || isOtpVerified"
          @click="verifyOtp()"
        >
          {{ isOtpVerifying ? 'Перевірка...' : (isOtpVerified ? 'OTP перевірено' : 'Перевірити OTP') }}
        </button>
        <div v-if="isOtpVerified" class="mini-kasko-order__payment-step">
          <div class="mini-kasko-order__payment-options">
            <label
              class="mini-kasko-order__otp-option"
              :class="{
                'mini-kasko-order__otp-option--active': paymentType === 'liqpay',
                'mini-kasko-order__otp-option--paid': isPaymentTypeAccepted('liqpay'),
                'mini-kasko-order__otp-option--disabled': isPaymentTypeSelectionDisabled,
              }"
            >
              <input
                :value="paymentType"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="liqpay"
                :disabled="isPaymentTypeSelectionDisabled"
                @change="paymentType = 'liqpay'"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>LiqPay</span>
              <span v-if="isPaymentTypeAccepted('liqpay')" class="mini-kasko-order__payment-badge">Сплачено</span>
            </label>
            <label
              class="mini-kasko-order__otp-option"
              :class="{
                'mini-kasko-order__otp-option--active': paymentType === 'ipay',
                'mini-kasko-order__otp-option--paid': isPaymentTypeAccepted('ipay'),
                'mini-kasko-order__otp-option--disabled': isPaymentTypeSelectionDisabled,
              }"
            >
              <input
                :value="paymentType"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="ipay"
                :disabled="isPaymentTypeSelectionDisabled"
                @change="paymentType = 'ipay'"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>iPay</span>
              <span v-if="isPaymentTypeAccepted('ipay')" class="mini-kasko-order__payment-badge">Сплачено</span>
            </label>
            <label
              class="mini-kasko-order__otp-option"
              :class="{
                'mini-kasko-order__otp-option--active': paymentType === 'hostpay',
                'mini-kasko-order__otp-option--paid': isPaymentTypeAccepted('hostpay'),
                'mini-kasko-order__otp-option--disabled': isPaymentTypeSelectionDisabled,
              }"
            >
              <input
                :value="paymentType"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="hostpay"
                :disabled="isPaymentTypeSelectionDisabled"
                @change="paymentType = 'hostpay'"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>HostPay</span>
              <span v-if="isPaymentTypeAccepted('hostpay')" class="mini-kasko-order__payment-badge">Сплачено</span>
            </label>
            <label
              v-if="isPortmoneAvailable"
              class="mini-kasko-order__otp-option"
              :class="{
                'mini-kasko-order__otp-option--active': paymentType === 'portmone',
                'mini-kasko-order__otp-option--paid': isPaymentTypeAccepted('portmone'),
                'mini-kasko-order__otp-option--disabled': isPaymentTypeSelectionDisabled,
              }"
            >
              <input
                :value="paymentType"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="portmone"
                :disabled="isPaymentTypeSelectionDisabled"
                @change="paymentType = 'portmone'"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>Розстрочка Portmone</span>
              <select
                v-if="paymentType === 'portmone' && !isPaymentTypeSelectionDisabled"
                v-model.number="portmoneInstallmentTerm"
                class="mini-kasko-order__portmone-term"
              >
                <option v-for="term in availablePortmoneInstallmentTerms" :key="term" :value="term">
                  {{ term }} міс.
                </option>
              </select>
              <span v-if="isPaymentTypeAccepted('portmone')" class="mini-kasko-order__payment-badge">Сплачено</span>
            </label>
            <label
              class="mini-kasko-order__otp-option"
              :class="{
                'mini-kasko-order__otp-option--active': paymentType === 'legal_invoice',
                'mini-kasko-order__otp-option--paid': isPaymentTypeAccepted('legal_invoice'),
                'mini-kasko-order__otp-option--disabled': isPaymentTypeSelectionDisabled,
              }"
            >
              <input
                :value="paymentType"
                class="mini-kasko-order__otp-option-input"
                type="radio"
                value="legal_invoice"
                :disabled="isPaymentTypeSelectionDisabled"
                @change="paymentType = 'legal_invoice'"
              >
              <span class="mini-kasko-order__otp-option-check"></span>
              <span>Рахунок-фактура</span>
              <span v-if="isPaymentTypeAccepted('legal_invoice')" class="mini-kasko-order__payment-badge">
                {{ isPaid ? 'Сплачено' : 'Квитанція' }}
              </span>
            </label>
          </div>
          <button
            v-if="!canConfirmByPayment"
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || !isOtpVerified || canConfirmByPayment"
            @click="createPaymentInvoice()"
          >
            {{ paymentActionLabel }}
          </button>
          <button
            v-if="paymentType === 'legal_invoice' && !canConfirmByPayment"
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || !legalInvoiceId"
            @click="$refs.receiptInput.click()"
          >
            {{ isReceiptUploading ? 'Завантаження...' : 'Завантажити квитанцію' }}
          </button>
          <input
            ref="receiptInput"
            class="mini-kasko-order__receipt-input"
            type="file"
            accept="image/*,application/pdf"
            @change="onReceiptChange"
          >
          <button
            v-if="paymentType !== 'legal_invoice' && !canConfirmByPayment"
            type="button"
            class="mini-kasko-order__action mini-kasko-order__action--secondary"
            :disabled="isProcessing || !isOtpVerified || canConfirmByPayment"
            @click="checkPaymentStatus()"
          >
            {{ isPaymentChecking ? 'Перевірка...' : 'Перевірити оплату' }}
          </button>
          <small
            class="mini-kasko-order__otp-send-message"
            :class="{
              'mini-kasko-order__otp-send-message--hidden': !paymentMessage,
              'mini-kasko-order__otp-send-message--error': isPaymentMessageError,
            }"
          >
            {{ paymentMessage }}
          </small>
        </div>
        <button
          v-if="canConfirmByPayment"
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--primary"
          :disabled="isProcessing || !isOtpVerified || !canConfirmByPayment"
          @click="confirmOrder()"
        >
          {{ isSubmitting ? 'Обробка...' : 'Оформити' }}
        </button>
      </div>
    </div>

    <MiniKaskoPaymentModal
      v-if="isPaymentModalVisible"
      :payment-link="paymentLink"
      :payment-qr-code="paymentQrCode"
      :payment-link-message="paymentLinkMessage"
      @close="closePaymentModal"
      @copy="copyPaymentLink"
      @go-to-link="goToPaymentLink"
    />
  </section>
</template>

<script>
import { HttpClient } from '@/shared/lib/http-client';
import { getPaymentSystemsList } from '@/api/paymentSystem';
import MiniKaskoPaymentModal from './MiniKaskoPaymentModal.vue';

export default {
  name: 'MiniKaskoOtpSection',
  components: { MiniKaskoPaymentModal },
  props: {
    policyNumber: { type: String, default: '' },
    orderId: { type: String, default: '' },
    price: { type: Number, default: null },
    earnings: { type: Number, default: null },
    companyId: { type: String, default: '' },
    insurantName: { type: String, default: '' },
    customerCode: { type: String, default: '' },
    preloadedOtpState: { type: Object, default: null },
  },
  emits: ['order-confirmed'],
  data() {
    return {
      otp: '',
      otpSendingChannel: 'email',
      isOtpVerified: false,
      isOtpVerifiedFromHistory: false,
      isOtpVerifying: false,
      isOtpSending: false,
      otpSendingMessage: '',
      isSubmitting: false,
      isPaid: false,
      hasUploadedLegalReceipt: false,
      canConfirmByPayment: false,
      acceptedPaymentType: '',
      payments: [],
      paymentType: 'liqpay',
      // fallback, якщо список платіжних систем не завантажився;
      // терміни мають збігатися з PORTMONE_INSTALLMENT_TERMS на бекенді,
      // відсотки — з payment_system_installment_terms в адмінці
      PORTMONE_DEFAULT_INSTALLMENT_TERMS: [3, 6],
      PORTMONE_DEFAULT_TERM_COMMISSION: { 3: 4, 6: 8.5 },
      portmoneInstallmentTerm: 3,
      PORTMONE_MIN_AMOUNT: 500,
      paymentSystemsList: [],
      paymentLink: '',
      paymentQrCode: '',
      paymentLinkType: '',
      paymentLinkMessage: '',
      isPaymentModalVisible: false,
      isPaymentCreating: false,
      isPaymentChecking: false,
      isReceiptUploading: false,
      paymentMessage: '',
      isPaymentMessageError: false,
      errorMessage: '',
      backendValidationErrors: {},
    };
  },
  computed: {
    isProcessing() {
      return this.isSubmitting
        || this.isOtpSending
        || this.isOtpVerifying
        || this.isPaymentCreating
        || this.isPaymentChecking
        || this.isReceiptUploading;
    },
    legalInvoiceId() {
      const payment = this.payments.find((item) => Number(item?.invoice?.type_id) === 8);

      return payment?.invoice?.id || null;
    },
    paymentActionLabel() {
      if (this.isPaymentCreating) {
        return 'Формування...';
      }

      if (this.isPaid) {
        return 'Оплачено';
      }

      if (this.hasUploadedLegalReceipt) {
        return 'Квитанція завантажена';
      }

      if (this.paymentType === 'legal_invoice') {
        return this.legalInvoiceId ? 'Завантажити рахунок-фактуру' : 'Сформувати рахунок-фактуру';
      }

      return 'Оплатити продукт';
    },
    isPaymentTypeSelectionDisabled() {
      return this.canConfirmByPayment;
    },
    portmonePaymentSystem() {
      return this.paymentSystemsList.find((item) => item.code === 'portmone') || null;
    },
    portmoneInstallmentTerms() {
      const terms = (this.portmonePaymentSystem?.partial_pay_periods ?? [])
        .map(Number)
        .filter((term) => term > 0);

      return terms.length ? terms : this.PORTMONE_DEFAULT_INSTALLMENT_TERMS;
    },
    // комісію Portmone оплачує агент, тому термін доступний лише якщо
    // заробіток агента покриває комісію за цей термін (як у GetAllPaymentWays)
    availablePortmoneInstallmentTerms() {
      return this.portmoneInstallmentTerms.filter((term) => this.isPortmoneTermAvailable(term));
    },
    isPortmoneAvailable() {
      if (this.isPaymentTypeAccepted('portmone')) {
        return true;
      }

      return Number(this.price) >= this.PORTMONE_MIN_AMOUNT
        && this.availablePortmoneInstallmentTerms.length > 0;
    },
    otpInputPlaceholder() {
      return this.isOtpVerifiedFromHistory ? 'OTP вже введено' : '';
    },
    parentUserId() {
      return this.$store.getters.USER_INFO?.parentId || this.$store.getters.USER?.id;
    },
    otpFieldError() {
      const value = this.backendValidationErrors['otp'];
      return Array.isArray(value) ? value[0] : '';
    },
  },
  mounted() {
    if (this.preloadedOtpState) {
      this.applyOtpAndPaymentFromState(this.preloadedOtpState);
    } else if (this.orderId) {
      this.fetchOtpState();
    }
  },
  watch: {
    orderId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.fetchOtpState();
      }
    },
    companyId: {
      immediate: true,
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.fetchPaymentSystems();
        }
      },
    },
    availablePortmoneInstallmentTerms: {
      immediate: true,
      handler(terms) {
        if (terms.length && !terms.includes(Number(this.portmoneInstallmentTerm))) {
          this.portmoneInstallmentTerm = terms[0];
        }
      },
    },
    isPortmoneAvailable(isAvailable) {
      if (!isAvailable && this.paymentType === 'portmone' && !this.isPaymentTypeSelectionDisabled) {
        this.paymentType = 'liqpay';
      }
    },
  },
  methods: {
    normalizeOrderId(value) {
      if (value === undefined || value === null || value === '' || value === 'undefined' || value === 'null') {
        return '';
      }

      return String(value);
    },
    resolveRequestErrorMessage(error, fallbackMessage) {
      const status = error?.response?.data?.status;
      const message = error?.response?.data?.message;

      if (status === 'validation' && message === 'The given data was invalid.') {
        return '';
      }

      return message || fallbackMessage;
    },
    focusErrorAlert() {
      this.$nextTick(() => {
        const alert = this.$refs.errorAlert;

        if (!alert) {
          return;
        }

        if (typeof alert.scrollIntoView === 'function') {
          alert.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }

        if (typeof alert.focus === 'function') {
          try {
            alert.focus({
              preventScroll: true,
            });
          } catch (focusError) {
            alert.focus();
          }
        }
      });
    },
    applyOtpAndPaymentFromState(state) {
      this.isPaid = Boolean(state.isPaid);
      this.hasUploadedLegalReceipt = Boolean(state.hasUploadedLegalReceipt);
      this.canConfirmByPayment = Boolean(state.canConfirmByPayment);
      this.acceptedPaymentType = state.acceptedPaymentType || '';
      this.payments = Array.isArray(state.payments) ? state.payments : [];
      if (this.acceptedPaymentType) {
        this.paymentType = this.acceptedPaymentType;
      }
      if (state.otpHistory?.isOtpVerified || state.otpHistory?.date_otp_entered) {
        this.isOtpVerified = true;
        this.isOtpVerifiedFromHistory = true;
      }
      if (this.isPaid) {
        this.paymentMessage = 'Продукт оплачено. Тепер можна підтвердити поліс.';
        this.isPaymentMessageError = false;
      } else if (this.hasUploadedLegalReceipt) {
        this.paymentMessage = 'Квитанція завантажена, можна підтвердити.';
        this.isPaymentMessageError = false;
      }
    },
    applyOtpAndPaymentFromOrder(order) {
      this.applyOtpAndPaymentFromState({
        isPaid: order.isPaid,
        hasUploadedLegalReceipt: order.hasUploadedLegalReceipt,
        canConfirmByPayment: order.canConfirmByPayment || order.isPaid || order.hasUploadedLegalReceipt,
        acceptedPaymentType: order.acceptedPaymentType || '',
        payments: order.payments || [],
        otpHistory: order.otpHistory || null,
      });
    },
    async fetchOtpState() {
      if (!this.orderId) return;
      try {
        const response = await HttpClient.get(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.orderId)}`);
        const order = response.data.data;
        this.applyOtpAndPaymentFromOrder(order);
      } catch {
        // ignore
      }
    },
    async fetchOrder() {
      if (!this.orderId) return;
      try {
        const response = await HttpClient.get(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.orderId)}`);
        this.applyOtpAndPaymentFromOrder(response.data.data);
      } catch {
        // ignore
      }
    },
    async sendOtp() {
      const channel = this.otpSendingChannel;
      this.isOtpSending = true;
      this.errorMessage = '';
      this.otpSendingMessage = '';

      try {
        await HttpClient.post(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.orderId)}/send-otp`, {
          channel,
        });

        this.otpSendingMessage = channel === 'email'
          ? 'Код відправлено на email.'
          : 'Код відправлено у Viber/SMS.';
      } catch (error) {
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося відправити OTP.');
        this.focusErrorAlert();
      } finally {
        this.isOtpSending = false;
      }
    },
    async verifyOtp() {
      this.isOtpVerifying = true;
      this.errorMessage = '';
      this.backendValidationErrors = {};

      try {
        await HttpClient.post(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.orderId)}/verify-otp`, {
          otp: this.otp,
        });

        this.isOtpVerified = true;
        this.isOtpVerifiedFromHistory = false;
      } catch (error) {
        this.isOtpVerified = false;
        const errors = error?.response?.data?.data || error?.response?.data?.errors;
        if (errors && typeof errors === 'object') {
          this.backendValidationErrors = errors;
        }
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося перевірити OTP.');
        this.focusErrorAlert();
      } finally {
        this.isOtpVerifying = false;
      }
    },
    handleOtpInput() {
      if (this.backendValidationErrors['otp']) {
        this.backendValidationErrors = {
          ...this.backendValidationErrors,
          otp: null,
        };
      }
      this.isOtpVerified = false;
      this.isOtpVerifiedFromHistory = false;
      this.paymentMessage = this.resolvePaymentStatusMessage();
    },
    async confirmOrder() {
      if (!this.isOtpVerified) {
        this.errorMessage = 'Спочатку перевірте OTP-код.';
        this.focusErrorAlert();
        return;
      }

      if (!this.canConfirmByPayment) {
        this.errorMessage = 'Спочатку оплатіть продукт або завантажте квитанцію.';
        this.focusErrorAlert();
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';
      this.backendValidationErrors = {};

      try {
        const payload = this.isOtpVerifiedFromHistory ? {} : { otp: this.otp };
        await HttpClient.post(`/insurance/mini-kasko/orders/${this.orderId}/confirm`, payload);

        this.$emit('order-confirmed');
      } catch (error) {
        const errors = error?.response?.data?.data || error?.response?.data?.errors;
        if (errors && typeof errors === 'object') {
          this.backendValidationErrors = errors;
        }
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося підтвердити OTP.');
        this.focusErrorAlert();
      } finally {
        this.isSubmitting = false;
      }
    },
    resolvePaymentStatusMessage() {
      if (this.isPaid) {
        this.isPaymentMessageError = false;
        return 'Продукт оплачено. Тепер можна підтвердити поліс.';
      }

      if (this.hasUploadedLegalReceipt) {
        this.isPaymentMessageError = false;
        return 'Квитанція завантажена, можна підтвердити.';
      }

      this.isPaymentMessageError = false;
      return '';
    },
    isPaymentTypeAccepted(type) {
      return this.acceptedPaymentType === type;
    },
    async fetchPaymentSystems() {
      try {
        const response = await getPaymentSystemsList(this.companyId);
        this.paymentSystemsList = Array.isArray(response.data) ? response.data : [];
      } catch {
        this.paymentSystemsList = [];
      }
    },
    getPortmoneTermCommission(term) {
      const ps = this.portmonePaymentSystem;
      const termData = (ps?.installment_terms ?? [])
        .find((item) => Number(item.term_months) === Number(term));

      if (termData?.percentage_acquiring !== undefined && termData?.percentage_acquiring !== null) {
        return Number(termData.percentage_acquiring);
      }

      return Number(this.PORTMONE_DEFAULT_TERM_COMMISSION[term] ?? ps?.percentage_acquiring ?? 0);
    },
    isPortmoneTermAvailable(term) {
      const price = Number(this.price);
      const commissionPercent = this.getPortmoneTermCommission(term);

      if (commissionPercent <= 0 || !(price > 0)) {
        return true;
      }

      // заробіток невідомий — не блокуємо на фронті, остаточно перевіряє бекенд
      if (this.earnings === null || this.earnings === undefined || Number.isNaN(Number(this.earnings))) {
        return true;
      }

      return Number(this.earnings) >= price * commissionPercent / 100;
    },
    resetPaymentLink() {
      this.paymentLink = '';
      this.paymentQrCode = '';
      this.paymentLinkType = '';
      this.paymentLinkMessage = '';
      this.isPaymentModalVisible = false;
    },
    openPaymentModal() {
      if (!this.paymentLink) {
        return;
      }

      this.isPaymentModalVisible = true;
    },
    closePaymentModal() {
      this.isPaymentModalVisible = false;
    },
    copyPaymentLink() {
      if (!this.paymentLink) {
        return;
      }

      this.$copyText(this.paymentLink);
      this.paymentLinkMessage = 'Посилання скопійовано.';
    },
    goToPaymentLink() {
      if (!this.paymentLink) {
        return;
      }

      window.location.href = this.paymentLink;
    },
    async createPaymentInvoice() {
      if (!this.isOtpVerified) {
        this.errorMessage = 'Спочатку перевірте OTP-код.';
        this.focusErrorAlert();
        return;
      }

      this.isPaymentCreating = true;
      this.errorMessage = '';
      this.paymentMessage = '';
      this.isPaymentMessageError = false;
      this.paymentLinkMessage = '';

      try {
        if (this.paymentType === 'legal_invoice') {
          this.resetPaymentLink();
          await this.createLegalInvoice();
          return;
        }

        const payload = {
          orderId: this.normalizeOrderId(this.orderId),
          amount: this.price,
          paidBy: this.paymentType === 'liqpay' ? 'client' : 'agent',
          provider: this.paymentType,
        };

        if (this.paymentType === 'portmone') {
          payload.type = 'portmone-installment';
          payload.installmentTerm = this.portmoneInstallmentTerm;
        }

        const response = await HttpClient.post('/orders/get-invoice', payload);
        const invoiceLink = response.data?.data?.invoiceLink;
        const qrCode = response.data?.data?.qrCode;

        if (!invoiceLink) {
          this.resetPaymentLink();
          this.paymentMessage = 'Рахунок сформовано. Перевірте оплату після сплати.';
          this.isPaymentMessageError = false;
          await this.fetchOrder();
          return;
        }

        this.paymentLink = invoiceLink;
        this.paymentQrCode = qrCode || '';
        this.paymentLinkType = this.paymentType;
        this.paymentMessage = 'Посилання на оплату сформовано. Після сплати натисніть "Перевірити оплату".';
        this.isPaymentMessageError = false;
        this.openPaymentModal();
        await this.fetchOrder();
      } catch (error) {
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося сформувати оплату.');
        this.focusErrorAlert();
      } finally {
        this.isPaymentCreating = false;
      }
    },
    async createLegalInvoice() {
      await HttpClient.post('/orders/get-invoice', {
        orderId: this.normalizeOrderId(this.orderId),
        amount: this.price,
        type: 'legal-entity-insurance',
        payParts: 2,
      });

      await this.fetchOrder();
      this.openLegalInvoice();
      this.paymentMessage = 'Рахунок-фактуру відкрито. Після оплати завантажте квитанцію.';
      this.isPaymentMessageError = false;
    },
    openLegalInvoice() {
      const params = new URLSearchParams(this.buildLegalInvoiceParams()).toString();
      const userId = this.parentUserId || this.$store.getters.USER?.id;

      window.open(`/api/orders/show-invoice-legal/${userId}/${this.normalizeOrderId(this.orderId)}?${params}`, '_blank');
    },
    buildLegalInvoiceParams() {
      return {
        company: this.insurantName,
        code: this.customerCode || '',
        price: this.price,
        mtsbuCode: this.policyNumber || this.orderId,
      };
    },
    async uploadReceipt(file) {
      if (!file) {
        return;
      }

      if (!this.legalInvoiceId) {
        this.errorMessage = 'Спочатку сформуйте рахунок-фактуру.';
        this.focusErrorAlert();
        return;
      }

      this.isReceiptUploading = true;
      this.errorMessage = '';

      try {
        await HttpClient.uploadFile(`/invoice/upload-receipt/${this.legalInvoiceId}`, file);
        await this.fetchOrder();
        this.hasUploadedLegalReceipt = true;
        this.canConfirmByPayment = true;
        this.paymentMessage = 'Квитанція завантажена, можна підтвердити.';
        this.isPaymentMessageError = false;
      } catch (error) {
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося завантажити квитанцію.');
        this.focusErrorAlert();
      } finally {
        this.isReceiptUploading = false;
      }
    },
    async checkPaymentStatus() {
      this.isPaymentChecking = true;
      this.errorMessage = '';
      this.paymentMessage = '';
      this.isPaymentMessageError = false;

      try {
        const response = await HttpClient.post(`/payments/${this.normalizeOrderId(this.orderId)}/check-invoice`);
        const paidInvoice = response.data?.data;

        if (paidInvoice) {
          this.isPaid = true;
          this.canConfirmByPayment = true;
          this.paymentMessage = 'Продукт оплачено. Тепер можна підтвердити поліс.';
          this.isPaymentMessageError = false;
          await this.fetchOrder();
          return;
        }

        this.isPaid = false;
        this.canConfirmByPayment = this.hasUploadedLegalReceipt;
        this.paymentMessage = response.data?.message || 'Оплату ще не знайдено.';
        this.isPaymentMessageError = true;
      } catch (error) {
        this.isPaid = false;
        this.canConfirmByPayment = this.hasUploadedLegalReceipt;
        this.paymentMessage = this.resolveRequestErrorMessage(error, 'Не вдалося перевірити оплату.');
        this.isPaymentMessageError = true;
      } finally {
        this.isPaymentChecking = false;
      }
    },
    onReceiptChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) {
        this.uploadReceipt(file);
      }
      event.target.value = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-order {
  &__alert {
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;

    &--error {
      background: #fef2f2;
      color: #b91c1c;
      border: 1px solid #fecaca;
    }

    &--success {
      background: #f0fdf4;
      color: #166534;
      border: 1px solid #bbf7d0;
    }

    &--warning {
      background: #fffbeb;
      color: #92400e;
      border: 1px solid #fde68a;
    }
  }

  &__card {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__group-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  &__subtitle {
    color: #52525b;
    font-size: 14px;
    line-height: 20px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    span {
      font-size: 13px;
      line-height: 18px;
      color: #52525b;
    }

    input,
    select {
      width: 100%;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #fff;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:focus {
        border-color: #fb923c;
        box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
      }

      &[readonly],
      &:disabled {
        background: #f8fafc;
        color: #52525b;
        cursor: default;
      }
    }
  }

  &__field-error {
    color: #dc2626;
    font-size: 12px;
    line-height: 16px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 190px;
    min-height: 48px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--secondary {
      background: #fff;
      color: #0a77ff;
      border: 1px solid #cbd5e1;
    }

    &--primary {
      background: #fb923c;
      color: #fff;
      border: 0;
    }
  }

  &__receipt-input {
    display: none;
  }

  &__otp {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: center;
  }

  &__otp-main {
    max-width: 560px;
  }

  &__otp-form {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    > .mini-kasko-order__action {
      margin-top: 24px;
    }
  }

  &__otp-send {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__otp-options {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: center;
  }

  &__otp-option {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 14px;
    line-height: 18px;
    color: #334155;
    cursor: pointer;

    &--active {
      color: #0f172a;
    }

    &--paid {
      color: #166534;
      font-weight: 600;
    }

    &--disabled {
      cursor: default;
    }

    &--disabled:not(&--paid) {
      color: #94a3b8;
    }

    &--active &-check {
      border-color: #fb923c;
    }

    &--paid &-check {
      border-color: #16a34a;
    }

    &--active &-check::after {
      opacity: 1;
    }

    &--paid &-check::after {
      background-color: #16a34a;
      opacity: 1;
    }
  }

  &__otp-option-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__otp-option-check {
    display: inline-flex;
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
    align-items: center;
    justify-content: center;
    border: 1px solid #94a3b8;
    border-radius: 50%;
    background-color: #fff;

    &::after {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #fb923c;
      opacity: 0;
    }
  }

  &__otp-send-message {
    display: block;
    min-height: 18px;
    color: #15803d;
    font-size: 13px;
    line-height: 18px;

    &--hidden {
      visibility: hidden;
    }

    &--error {
      color: #dc2626;
    }
  }

  &__payment-badge {
    display: inline-flex;
    align-items: center;
    min-height: 18px;
    padding: 1px 6px;
    border-radius: 4px;
    background-color: #dcfce7;
    color: #166534;
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
  }

  &__portmone-term {
    min-height: 26px;
    padding: 2px 6px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    color: #0f172a;
    font-size: 13px;
    line-height: 18px;
    outline: none;
  }

  &__payment-step {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__payment-options {
    display: flex;
    max-width: 420px;
    flex-wrap: wrap;
    gap: 10px 14px;
  }

  @media (max-width: 640px) {
    &__otp,
    &__otp-form {
      flex-direction: column;
      align-items: stretch;
    }

    &__otp-form {
      > .mini-kasko-order__action {
        margin-top: 0;
      }
    }

    &__payment-step {
      margin-top: 0;
    }

    &__actions {
      flex-direction: column;
      align-items: stretch;
    }

    &__action {
      width: 100%;
    }
  }
}
</style>
